import { useCallback, useState } from 'react';

import PropTypes from 'prop-types';

import { PaymentMethodsProvider } from '@zenbusiness/application-commons-ecommerce';

import { useNotification } from '#client/App/NotificationManager';
import useFlowConfig from '#client/hooks/flow/useFlowConfig';
import useFlowState from '#client/hooks/flow/useFlowState';
import useFlowNavigation from '#client/hooks/useFlowNavigation';

import { SECTION_KEYS } from '../../constants/sections';
import { useCheckout } from '../../hooks/useCheckout';
import CheckoutCardList from '../CheckoutCardList';
import PostOrderScreen from '../PostOrderScreen';

function CheckoutForm({ sections }) {
  const { formMethods } = useCheckout();
  const { onError } = useNotification();
  const { postOrderAutoLogin } = useFlowConfig();
  const { goToNextPage, nextPage } = useFlowNavigation();
  const { get, set, clear } = useFlowState();

  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState(null);

  // Check if order is completed
  const orderUuid = get('orderUuid');
  const isOrderCompleted = !!orderUuid;
  const nextFlowPage = nextPage?.path;

  // When we initially render with a completed order
  if (isOrderCompleted && postOrderAutoLogin && !showLoginDialog) {
    const formValues = formMethods.getValues();
    const { email, password } = formValues?.[SECTION_KEYS.ACCOUNT_INFO] ?? {};

    // If credentials are missing, go to next page
    if (!email || !password) {
      goToNextPage();
    } else {
      // Show login dialog with credentials
      setLoginCredentials({ email, password });
      setShowLoginDialog(true);
    }
  }

  const handleOrderSuccess = useCallback(() => {
    // Extract credentials
    const formValues = formMethods.getValues();
    const { email, password } = formValues?.[SECTION_KEYS.ACCOUNT_INFO] ?? {};

    // If auto-login is not enabled, simply go to the next page
    if (!postOrderAutoLogin) {
      goToNextPage();
      return;
    }

    // If credentials are missing, go to next page
    if (!email || !password) {
      goToNextPage();
      return;
    }

    // Flow state management
    if (nextFlowPage) {
      set('lastPage', nextFlowPage);
    } else {
      clear();
    }

    // Show login dialog with credentials
    setLoginCredentials({ email, password });
    setShowLoginDialog(true);
  }, [formMethods, postOrderAutoLogin, goToNextPage, nextFlowPage, set, clear]);

  return (
    <>
      <PaymentMethodsProvider onError={onError}>
        <CheckoutCardList sections={sections} onOrderSuccess={handleOrderSuccess} />
      </PaymentMethodsProvider>

      {showLoginDialog && loginCredentials && (
        <PostOrderScreen loginCredentials={loginCredentials} />
      )}
    </>
  );
}

CheckoutForm.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default CheckoutForm;
